/**
 * How a run's counts, dates and durations read, in words.
 *
 * Shared because BOTH halves now render the same facts off the same wire
 * fields: the popup has shown "refreshed 3 days ago" since capture existed, and
 * Wallet's Refresh page shows it for the same `last_as_of`. Two copies of this
 * would disagree the first time one of them handled a boundary -- which has
 * already happened once in this repo for a different pair of formatters.
 *
 * `extension/src/popupView.ts` re-exports both so its existing call sites and
 * tests are unchanged.
 */

/**
 * WHOLE days from a YYYY-MM-DD to `now`, in LOCAL time; Infinity when the date
 * is missing or unparseable. Parsed field by field because `new Date(day)` reads
 * a bare date as UTC midnight, and rounded because two local midnights are 23 or
 * 25 hours apart across a clock change.
 */
export function daysSince(day: string | null | undefined, now: Date): number {
  if (!day) return Infinity;
  const [y, m, d] = day.split("-").map(Number);
  if (!y || !m || !d) return Infinity;
  const then = new Date(y, m - 1, d);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((today.getTime() - then.getTime()) / 86_400_000);
}

/**
 * "today", "yesterday", "N days ago" for a YYYY-MM-DD in LOCAL time.
 *
 * A future date reads as "today" rather than "-1 days ago": a clock skew between
 * the server's date and this machine's is not worth a sentence of its own.
 */
export function ago(day: string | null, now: Date): string {
  const days = daysSince(day, now);
  if (!Number.isFinite(days)) return "never";
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  return `${days} days ago`;
}

/** How long something has been going, in words: "18s", "2m 56s". */
export function elapsed(since: number, now: number): string {
  const seconds = Math.max(0, Math.round((now - since) / 1000));
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${String(seconds % 60).padStart(2, "0")}s`;
}

/** "1 program", "2 programs": a count and its noun, with the s only when earned. */
export function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}
